import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { isApiError } from '@/api'
import { removeMember } from '@/api/members'
import RemoveMemberConfirmDialog from '@/components/RemoveMemberConfirmDialog'
import { useAnalytics } from '@/hooks/useAnalytics'
import { MY_ACTIVE_COST_CENTER_QUERY_KEY } from '@/pages/app/Team/Department/utils'
import { EventName } from '@/utils/eventDefinitions'

interface RemoveMemberDialogProps {
  userId: string
  displayName: string
  /** Recent-usage sentence from `getRemovalUsageLine`, shown ahead of the warning. */
  usageLine?: string | null
  scope: 'team' | 'organization'
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

const RemoveMemberDialog = ({
  userId,
  displayName,
  usageLine,
  scope,
  open,
  onOpenChange,
  onSuccess,
}: RemoveMemberDialogProps) => {
  const [error, setError] = React.useState<string | null>(null)
  const queryClient = useQueryClient()
  const { trackEvent } = useAnalytics()

  const mutation = useMutation({
    mutationFn: () => removeMember(userId),
    onSuccess: () => {
      // Broad invalidation covers both the list (`['team-members', 'table', ...]`)
      // and any detail-query caches keyed under `['team-members', 'detail', userId]`.
      queryClient.invalidateQueries({ queryKey: ['team-members'] })
      // Removing a member shifts the CC-wide snapshot the Create-Department
      // sole-admin check reads — keep them in lockstep.
      queryClient.invalidateQueries({
        queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY],
      })
      trackEvent?.(EventName.MemberRemoved, { user_id: userId })
      onOpenChange(false)
      onSuccess()
    },
    onError: (err: unknown) => {
      // `ApiError` is a plain object, so `instanceof Error` never matched and every
      // server message (including the SCIM-managed 403) fell back to the generic string.
      const message = isApiError(err) ? (err.detail ?? err.message) : 'Failed to remove member. Please try again.'
      setError(message)
    },
  })

  return (
    <RemoveMemberConfirmDialog
      isOpen={open}
      name={displayName}
      usageLine={usageLine ?? null}
      scope={scope}
      isRemoving={mutation.isPending}
      error={error}
      onConfirm={() => {
        setError(null)
        mutation.mutate()
      }}
      onCancel={() => {
        onOpenChange(false)
        setError(null)
      }}
    />
  )
}

export default RemoveMemberDialog
